import { Badge, TextTooltip } from '@components'
import { IAttachmentAnalysisBadge } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import styled, { css } from 'styled-components'

interface FileAnalysisBadgesProps {
  badges?: IAttachmentAnalysisBadge[]
  fileId?: number
  alignEnd?: boolean
}

const getBadgeDotVariant = (
  badgeKey: string,
  badgeLabel?: string
): 'date' | 'amount' | 'default' => {
  if (badgeKey === 'primary_date' && badgeLabel) return 'date'
  if (badgeKey === 'total_amount') return 'amount'
  return 'default'
}

const getBadgeTooltip = (badgeLabel?: string) => badgeLabel || undefined

const getBadgeValue = (badgeKey: string, badgeValue: string) => {
  if (badgeKey === 'primary_date') return formatDate(badgeValue)
  return badgeValue
}

export const FileAnalysisBadges: React.FC<FileAnalysisBadgesProps> = ({
  badges = [],
  fileId,
  alignEnd = false
}) => {
  if (badges.length === 0) return null

  return (
    <ItemBadgeList $alignEnd={alignEnd}>
      {badges.map((badge, index) => {
        const tooltip = getBadgeTooltip(badge.label)
        const badgeNode = (
          <ItemBadgeWrapper key={`${fileId || 'file'}-${badge.key}-${badge.value}-${index}`}>
            <Badge type="default">
              <ItemBadgeText>
                <ItemBadgeDot $variant={getBadgeDotVariant(badge.key, badge.label)} />
                {getBadgeValue(badge.key, badge.value)}
              </ItemBadgeText>
            </Badge>
          </ItemBadgeWrapper>
        )

        if (!tooltip) {
          return badgeNode
        }

        return (
          <TextTooltip
            key={`${fileId || 'file'}-${badge.key}-${badge.value}-${index}`}
            tooltip={tooltip}
            placement="bottom"
          >
            {badgeNode}
          </TextTooltip>
        )
      })}
    </ItemBadgeList>
  )
}

const ItemBadgeList = styled.div<{ $alignEnd: boolean }>`
  display: flex;
  column-gap: ${({ theme }) => theme.spacing.xs}rem;
  row-gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex-wrap: wrap;
  min-width: 0;

  ${({ $alignEnd }) =>
    $alignEnd &&
    css`
      margin-left: auto;
      justify-content: flex-end;
      flex: 3 1 0;
    `}
`

const ItemBadgeText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  max-width: 18rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`

const ItemBadgeWrapper = styled.span`
  display: inline-flex;
`

const ItemBadgeDot = styled.span<{ $variant: 'date' | 'amount' | 'default' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme, $variant }) => {
    if ($variant === 'date') return theme.colors.primary
    if ($variant === 'amount') return theme.colors.success
    return theme.colors.neutralGray
  }};
`
